import { SendEventParams, useUserEventLogger } from '@repo/tracking-service';
import { BaseTopNavigation } from '@repo/ui';
import { ColorPalette, IconType, TextButtonStateType, TopNavigation } from '@zenterprise-inc/ui';
import { BasicTopNavigationProps } from '@zenterprise-inc/ui';
import cn from 'clsx';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { HTMLAttributes, ReactNode } from 'react';

import s from './Layout.module.scss';
import Notice from './Notice';

import { COMMON_PATHS } from '@/lib/constants/paths';

type ClickEvent = {
  name: string;
  attributes?: SendEventParams['attributes'];
};

type LayoutRightIconModel = {
  icon: IconType;
  color?: ColorPalette;
  onClick?: () => void;
  path?: string;
  clickEvent?: ClickEvent;
};

type LayoutProps = {
  title?: ReactNode;
  leftIcon?: {
    icon: BasicTopNavigationProps['leftIcon'];
    onClick?: () => void;
    clickEvent?: ClickEvent;
  };
  rightIcons?: LayoutRightIconModel[];
  rightTextButton?: {
    label: string;
    state?: TextButtonStateType;
    onClick?: () => void;
    path?: string;
    clickEvent?: ClickEvent;
  };
  className?: HTMLAttributes<HTMLDivElement>['className'];
  mainClassName?: HTMLAttributes<HTMLDivElement>['className'];
  backgroundColor?: string;
  children: ReactNode;
  footerContent?: ReactNode;
};

const Layout = ({ children, leftIcon, rightIcons, rightTextButton, className, mainClassName, backgroundColor, footerContent, ...props }: LayoutProps) => {
  const router = useRouter();
  const { sendClickEvent } = useUserEventLogger();

  const handleTopNavigationButtonAction = (path?: string, callback?: () => void, clickEvent?: ClickEvent) => {
    if (clickEvent) {
      sendClickEvent(clickEvent.name, clickEvent.attributes);
    }
    if (path) {
      router.push(`${path}?orgAlias=${router.query?.orgAlias}`);
      return;
    }

    callback?.();
  };

  const topNavigationIcons = rightIcons?.map((item: LayoutRightIconModel) => ({
    icon: item.icon,
    color: item.color,
    onClick: () => {
      handleTopNavigationButtonAction(item.path, item.onClick, item.clickEvent);
    }
  }));

  const topNavigationTextButton = {
    label: rightTextButton?.label || '',
    state: rightTextButton?.state,
    onClick: () => {
      handleTopNavigationButtonAction(rightTextButton?.path, rightTextButton?.onClick, rightTextButton?.clickEvent);
    }
  };

  return (
    <div className={cn('w-full min-h-screen flex flex-col', className, { ['bg-gray-50']: backgroundColor === '#F3F7F8' })}>
      {backgroundColor && (
        <Head>
          <meta name={'theme-color'} content={backgroundColor} />
          <meta name="apple-mobile-web-app-status-bar-style" content={backgroundColor} />
        </Head>
      )}
      <TopNavigation.Basic
        className={cn('z-[2] w-full', {
          ['!bg-gray-50']: backgroundColor === '#F3F7F8'
        })}
        onLeftClick={() => {
          if (leftIcon?.icon === 'back') {
            if (leftIcon?.clickEvent) {
              sendClickEvent(leftIcon.clickEvent.name, leftIcon.clickEvent.attributes);
            }
            router.back();
            return;
          }
          handleTopNavigationButtonAction(undefined, leftIcon?.onClick, leftIcon?.clickEvent);
        }}
        leftIcon={leftIcon?.icon}
        rightIcons={rightIcons && topNavigationIcons}
        rightTextButton={rightTextButton && topNavigationTextButton}
        leftIconColor={'gray.800'}
        {...props}
      />
      <Notice />
      <main className={cn('mx-auto w-full max-w-[520px] flex-1', mainClassName, s.main_top_margin)}>{children}</main>
      {footerContent && <footer>{footerContent}</footer>}
    </div>
  );
};

export const LogoLayout = ({ children, footerContent }: { children: ReactNode; footerContent: ReactNode }) => {
  const router = useRouter();
  return (
    <>
      <header>
        <BaseTopNavigation
          leftAction={
            <div className={'cursor-pointer'} onClick={() => router.push(COMMON_PATHS.환급랜딩, undefined, { scroll: true })}>
              <img
                src={`${process.env.NEXT_PUBLIC_RESOURCE_CENTER_URL}/common/logo/bznav/logo_bznav-refund.svg`}
                className={'px-normal-small'}
                alt={'bznav refund logo'}
              />
            </div>
          }
        />
      </header>
      <main className={'w-full flex-1 flex flex-col items-center'}>{children}</main>
      {footerContent && <footer className={'w-full'}>{footerContent}</footer>}
    </>
  );
};

export default Layout;
